import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dialog, DialogContent, IconButton } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import * as Loaders from '~/components/Loaders';
import api from '~/services';
import { integrationConfig, log } from '~/helpers';
import * as lib from '~/dependencies';
import Tabs from './Tabs';
import ListTickets from './List';
import NewTickets from './New';
import { Title } from './styles';

const { AUTO_TASK } = integrationConfig;

const endpointName = {
  18: 'zendesk/tickets',
  56: 'auto_task/tickets',
};

export default function Tickets({
  open,
  onClose,
  externalId,
  callId,
  integrationId,
}) {
  // STATES
  const [allTickets, setAllTickets] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [shouldDataUpdate, setShouldDataUpdate] = useState(false);
  const [loading, setLoading] = useState(false);

  const reducerTicketList = useSelector(state => state.integrations.tickets);

  useEffect(() => {
    const source = lib.axios.CancelToken.source();
    async function getTickets() {
      try {
        setLoading(true);

        const response = await api.integrations.get.getTickets(
          endpointName[integrationId],
          externalId,
          callId,
          {
            cancelToken: source.token,
          }
        );

        let fieldsInfo;
        if (integrationId === AUTO_TASK) {
          fieldsInfo = (
            await api.integrations.get.getTicketFields({
              cancelToken: source.token,
            })
          ).data;
        }

        if (response.data?.length) {
          const responseTicketList = lib._.map(response.data, ticket => {
            let autoTasksAdditionalFieldsInfo = {};
            if (integrationId === AUTO_TASK) {
              autoTasksAdditionalFieldsInfo = {
                priorityLabel: fieldsInfo?.priorities.find(
                  element => element.value === String(ticket.priority)
                )?.label,
                statusLabel: fieldsInfo?.status.find(
                  element => element.value === String(ticket.status)
                )?.label,
                queueLabel: fieldsInfo?.queues.find(
                  element => element.value === String(ticket.queue_id)
                )?.label,
              };
            }

            return {
              callId,
              integrationId,
              externalId,
              ...ticket,
              created_at: lib
                .dayjs(ticket.created_at)
                .format('ddd DD MMMM hh:mm:ss'),
              due_at: ticket.due_at
                ? lib.dayjs(ticket.due_at).format('ddd DD MMMM hh:mm:ss')
                : ticket.due_at,
              ...autoTasksAdditionalFieldsInfo,
            };
          });

          // Filter local tickets (redux) by callId
          const reducerLickedTicketList = reducerTicketList?.filter(
            ticket => ticket.callId === callId
          );

          // Merge Stored Locally + API response then filter for unique items
          const uniqueTickets = [
            ...new Map(
              [...reducerLickedTicketList, ...responseTicketList].map(t => [
                t.ticket_id,
                t,
              ])
            ).values(),
          ];

          setAllTickets(uniqueTickets);
          setLoading(false);
          setCurrentTab(0);
        }
      } catch (err) {
        log.error(`Failure to retrive the AutoTask tickets. Reason: `, err);
      } finally {
        setLoading(false);
      }
    }

    if (open) getTickets();

    return () => source.cancel('Axios request canceled.');
    // eslint-disable-next-line
  }, [
    callId,
    externalId,
    integrationId,
    shouldDataUpdate,
    reducerTicketList,
    open,
  ]);

  function updateTicketList() {
    setShouldDataUpdate(!shouldDataUpdate);
    setCurrentTab(0);
  }

  return (
    <Dialog open={open} aria-labelledby="ticket-dialog" fullWidth maxWidth="md">
      <Title disableTypography>
        <span style={{ marginLeft: 10 }}>Tickets</span>
        <IconButton aria-label="close" style={{ padding: 3 }} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Title>
      <DialogContent>
        <Tabs tab={currentTab} onSwitch={nextTab => setCurrentTab(nextTab)} />
        <div style={{ marginTop: 15, marginBottom: 25 }}>
          {currentTab ? (
            <NewTickets
              integrationId={integrationId}
              externalId={externalId}
              callId={callId}
              onCreate={updateTicketList}
            />
          ) : (
            <>
              {loading ? (
                <Loaders.Accordion numRows={3} />
              ) : (
                <ListTickets
                  onTicketLinkedToCall={updateTicketList}
                  tickets={allTickets}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

Tickets.propTypes = {
  open: lib.PropTypes.bool.isRequired,
  onClose: lib.PropTypes.func.isRequired,
  externalId: lib.PropTypes.string.isRequired,
  integrationId: lib.PropTypes.number.isRequired,
  callId: lib.PropTypes.string.isRequired,
};
